/**
 * Public careers API client.
 *
 * Talks to student-agent-api /careers/*. No auth — the people we hire mostly
 * don't have a Lomi account, and a signup wall in front of a job application
 * loses us the best of them.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export interface ScreeningQuestion {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'select';
  required?: boolean;
  options?: string[];
}

export interface RoleSummary {
  slug: string;
  title: string;
  team: string;
  team_label: string;
  location: string | null;
  employment_type: string;
  seniority: string | null;
  openings: number;
  comp_min_etb: number | null;
  comp_max_etb: number | null;
  comp_unit: string;
  comp_note: string | null;
  summary: string | null;
  tags: string[];
  apply_by: string | null;
}

export interface RoleDetail extends RoleSummary {
  body_md: string;
  screening_questions: ScreeningQuestion[];
}

export interface TeamGroup {
  team: string;
  label: string;
  roles: RoleSummary[];
}

export interface OpeningsResponse {
  teams: TeamGroup[];
  total_roles: number;
  total_openings: number;
}

export interface ApplicationPayload {
  full_name: string;
  phone: string;
  email?: string;
  telegram_username?: string;
  city?: string;
  current_position?: string;
  years_experience?: number;
  subjects?: string[];
  portfolio_url?: string;
  cv_url?: string;
  answers: Record<string, string>;
  source?: string;
  referrer?: string;
}

export interface ApplicationResult {
  success: boolean;
  reference: string;
  message: string;
  updated: boolean;
}

export const EMPLOYMENT_LABELS: Record<string, string> = {
  full_time: 'Full-time',
  part_time: 'Part-time',
  contract: 'Contract',
  freelance: 'Freelance',
  stipend: 'Stipend',
};

/** "25,000 – 38,000 ETB / month", or "15 – 30 ETB per item". */
export function formatPay(r: RoleSummary): string | null {
  if (!r.comp_min_etb) return null;
  const n = (v: number) => v.toLocaleString('en-US');
  const range =
    r.comp_max_etb && r.comp_max_etb !== r.comp_min_etb
      ? `${n(r.comp_min_etb)} – ${n(r.comp_max_etb)}`
      : n(r.comp_min_etb);
  const unit = r.comp_unit === 'item' ? 'per item' : `/ ${r.comp_unit}`;
  return `${range} ETB ${unit}`;
}

export async function fetchOpenings(): Promise<OpeningsResponse> {
  const res = await fetch(`${API_BASE_URL}/careers`);
  if (!res.ok) throw new Error('Could not load open roles');
  return res.json();
}

export async function fetchRole(slug: string): Promise<RoleDetail> {
  const res = await fetch(`${API_BASE_URL}/careers/${encodeURIComponent(slug)}`);
  if (res.status === 404) throw new Error('That role is no longer open.');
  if (!res.ok) throw new Error('Could not load this role');
  return res.json();
}

export async function submitApplication(
  slug: string,
  payload: ApplicationPayload
): Promise<ApplicationResult> {
  const res = await fetch(`${API_BASE_URL}/careers/${encodeURIComponent(slug)}/apply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // 429 is the rate limiter; anything else carries a `detail` we can show.
    const msg =
      res.status === 429
        ? 'Too many applications from this connection. Please try again in an hour.'
        : data?.detail || 'Something went wrong. Please try again.';
    throw new Error(typeof msg === 'string' ? msg : 'Please check your answers.');
  }
  return data as ApplicationResult;
}
